import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppNavigation } from '@/lib/navigation';
import { useAuth } from '@/context/auth/AuthContext';
import { supabase } from '@/lib/supabase';
import { ProgressIndicator } from '@/components/ui/ProgressIndicator';
import { NavigationControls } from '@/components/assessment/NavigationControls';
import { CurrentStateQuestions } from './questions/CurrentStateQuestions';
import { AspirationsQuestions } from './questions/AspirationsQuestions';
import { SkillsQuestions } from './questions/SkillsQuestions';
import { FutureVisionQuestions } from './questions/FutureVisionQuestions';
import { CompletionCelebration } from './CompletionCelebration';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { 
  Achievement, 
  CareerLevel, 
  UserProgress, 
  CareerPathNode, 
  CAREER_LEVELS, 
  ACHIEVEMENTS 
} from '@/features/careerAdventure/types/gamification';
import { useCareerProgress } from '@/hooks/useCareerProgress';
import { ProgressService } from '@/features/careerAdventure/services/ProgressService';
import { AssessmentComplete } from '@/components/assessment/AssessmentComplete';
import { useAssessmentReducer } from '@/hooks/useAssessmentReducer';
import { generateLearningPath } from '../features/careerAdventure/services/LearningPathService';
import { determineCareerStage, generateStaticLearningPath } from '@/features/careerAdventure/utils/careerUtils';
import { CareerStage } from '@/types/onboarding';
import { useCareerPath } from '@/context/CareerContext';
import { useCareerPathDetails } from '@/context/CareerPathContext';

const assessmentSections = ['currentState', 'aspirations', 'skills', 'futureVision'];

export function InitialAssessment() {
  const { user } = useAuth();
  const { careerPath } = useCareerPath();
  const { pathDetails } = useCareerPathDetails();
  const { navigateTo } = useAppNavigation();
  const { completeAssessment: completeCareerProgress, completeSection } = useCareerProgress();
  const [isLoading, setIsLoading] = useState(true);
  const [uiError, setUiError] = useState<string | null>(null);
  const [generatedPath, setGeneratedPath] = useState<CareerPathNode | null>(null);
  const [newAchievements, setNewAchievements] = useState<Achievement[]>([]);
  const [levelUp, setLevelUp] = useState<CareerLevel | null>(null);
  const [userProgress, setUserProgress] = useState<UserProgress | null>(null);
  
  const {
    state,
    dispatch,
    saveProgress,
    handleResponse,
    isValid,
    loadSavedProgress,
    completeAssessment
  } = useAssessmentReducer('initial');

  const progressService = useMemo(() => {
    if (!user?.id) return null;
    return new ProgressService(user.id);
  }, [user?.id]);

  useEffect(() => {
    const initializeAssessment = async () => {
      if (!user?.id || !progressService) return;
      
      try {
        setIsLoading(true);
        setUiError(null);
        
        // Load user progress first
        const progress = await progressService.getUserProgress();
        setUserProgress(progress);
        
        // Load saved assessment state
        await loadSavedProgress(user.id);
      } catch (err) {
        setUiError('Failed to load assessment state');
        console.error('Error loading assessment:', err);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAssessment();
  }, [user?.id, progressService, loadSavedProgress]);

  const saveAssessmentState = async (updates: Partial<typeof state>) => {
    if (!user?.id) return;
    
    try {
      const fullUpdates = {
        ...state,
        ...updates,
        user_id: user.id,
        assessment_type: 'initial',
        updated_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('assessment_states')
        .update(fullUpdates)
        .eq('user_id', user.id)
        .eq('assessment_type', 'initial')
        .select()
        .single();

      if (error) {
        // If no rows were updated, insert instead
        if (error.code === 'PGRST116') {
          const { data: newData, error: insertError } = await supabase
            .from('assessment_states')
            .insert(fullUpdates)
            .select()
            .single();

          if (insertError) throw insertError;
          return newData;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error saving assessment state:', error);
      throw error;
    }
  };

  const handleResponseChange = async (response: any) => {
    if (!user?.id) return;
    
    try {
      setUiError(null);
      handleResponse(state.current_step.toString(), response);
      await saveProgress(user.id);
    } catch (error) {
      setUiError('Failed to save your response. Please try again.');
      console.error('Failed to save response:', error);
    }
  };

  const handleNext = async () => {
    if (!user?.id) return;
    if (!isValid(state.current_step)) {
      setUiError('Please complete all required fields before continuing.');
      return;
    }
    
    try {
      setUiError(null);
      const nextStep = state.current_step + 1;
      
      if (nextStep >= totalSteps) {
        await handleComplete();
      } else {
        await saveAssessmentState({
          current_step: nextStep,
          last_completed_step: state.current_step
        });
        
        dispatch({ type: 'NEXT_STEP' });
        
        // Mark section as complete in progress tracking
        await completeSection(assessmentSections[state.current_step]);
      }
    } catch (error) {
      setUiError('Failed to save progress. Please try again.');
      console.error('Failed to navigate:', error);
    }
  };

  const handlePrevious = async () => {
    if (!user?.id || state.current_step === 0) return;
    
    try {
      setUiError(null);
      await saveAssessmentState({
        current_step: state.current_step - 1
      });
      
      dispatch({ type: 'PREV_STEP' });
    } catch (error) {
      setUiError('Failed to navigate. Please try again.');
      console.error('Failed to navigate:', error);
    }
  };

  const handleComplete = async () => {
    if (!user?.id) return;
    
    try {
      setIsLoading(true);
      setUiError(null);
      
      if (state.current_step < totalSteps) {
        await completeSection(assessmentSections[state.current_step]);
      }

      const careerStage = determineCareerStage(state.answers);
      const learningPath = generateStaticLearningPath(careerStage);
      
      setGeneratedPath(learningPath);
      await completeAssessment(careerStage, learningPath);

      const { achievements, levelUpData } = await completeCareerProgress(careerStage);

      if (achievements?.length) {
        setNewAchievements(achievements);
      }

      if (levelUpData) {
        setLevelUp(levelUpData);
      }

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({
          career_stage: careerStage,
          career_path: learningPath.title,
          onboarding_progress: {
            current_step: 'assessment_completed',
            completed_steps: ['initial_assessment'],
            next_step: 'skill_development'
          },
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

    } catch (err) {
      setUiError('Failed to complete assessment');
      console.error('Error completing assessment:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const totalSteps = 4;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (uiError || state.error) {
    return (
      <div className="text-center text-red-600">
        <p>{uiError || state.error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg"
        >
          Retry
        </button>
      </div>
    );
  }

  if (state.completed) {
    return (
      <AssessmentComplete
        type="initial"
        coinsEarned={100}
        learningPath={generatedPath}
        achievements={newAchievements}
        levelUp={levelUp}
        onContinue={() => navigateTo('/dashboard')}
      />
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <ProgressIndicator 
        currentStep={state.current_step} 
        totalSteps={totalSteps} 
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={state.current_step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          {state.current_step === 0 && (
            <CurrentStateQuestions
              responses={state.answers[0]}
              onChange={handleResponseChange}
            />
          )}
          {state.current_step === 1 && (
            <AspirationsQuestions
              responses={state.answers[1]}
              onChange={handleResponseChange}
            />
          )}
          {state.current_step === 2 && (
            <SkillsQuestions
              responses={state.answers[2]}
              onChange={handleResponseChange}
            />
          )}
          {state.current_step === 3 && (
            <FutureVisionQuestions
              responses={state.answers[3]}
              onChange={handleResponseChange}
            />
          )}
        </motion.div>
      </AnimatePresence>

      <NavigationControls
        currentStep={state.current_step}
        totalSteps={totalSteps}
        onNext={handleNext}
        onBack={handlePrevious}
        isComplete={state.current_step === totalSteps - 1}
      />
    </div>
  );
}